import React, { useEffect, useState } from 'react';
import useMealContext from 'contexts/meal.context';
import { MetadataType } from 'pages/create-brand/create-brand-metadata/types';
import { MetadataItemType } from 'pages/metadata/types';
import { apiService } from 'services/base.service';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import MetadataDropdown from 'components/metadata/metadata-dropdown';
import useDashboardContext from 'contexts/dashboard.context';

const CREATE_ENDPOINT = {
  meal: 'keywords',
  descriptive: 'keywords/meal_descriptvie_metadata',
  business: 'keywords/meal_business_metadata'
};

const MealMetadataInfo = () => {
  const {
    formData,
    setFormData,
    selectedKeywords,
    selectedBusinessMetadata,
    selectedDescriptiveMetadata,
    setSelectedBusinessMetadata,
    setSelectedDescriptiveMetadata,
    setSelectedKeywords
  } = useMealContext();
  const [keywordsList, setKeywordsList] = useState<MetadataType[]>([]);
  const [descriptiveMetadataList, setDescriptiveMetadataList] = useState<MetadataType[]>([]);
  const [businessMetadataList, setBusinessMetadataList] = useState<MetadataType[]>([]);
  const { category } = useDashboardContext();

  const MAP_LOCAL_STATE = {
    brand: setKeywordsList,
    meal: setKeywordsList,
    descriptive: setDescriptiveMetadataList,
    business: setBusinessMetadataList
  };

  const getMealkeywords = async () => {
    try {
      const res = await apiService.get('keywords', {
        params: { type: 'meal' }
      });
      const data = res.data?.data as MetadataItemType[];
      setKeywordsList(data);
    } catch {
      setKeywordsList([]);
    }
  };

  const getMealBusinessMetaData = async () => {
    try {
      const res = await apiService.get('keywords/meal_business_metadata');
      const data = res.data?.data as MetadataItemType[];
      setBusinessMetadataList(data);
    } catch {
      setBusinessMetadataList([]);
    }
  };

  const getMealDescriptiveMetaData = async () => {
    try {
      const res = await apiService.get('keywords/meal_descriptvie_metadata');
      const data = res.data?.data as MetadataItemType[];
      setDescriptiveMetadataList(data);
    } catch {
      setDescriptiveMetadataList([]);
    }
  };

  useEffect(() => {
    getMealkeywords();
    getMealBusinessMetaData();
    getMealDescriptiveMetaData();
  }, []);

  const onKeywordTagClick = (item: MetadataItemType) => {
    const exists = !!selectedKeywords.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedKeywords([...selectedKeywords, item]);
    } else {
      const filtered = selectedKeywords.filter((w) => w.id !== item.id);
      setSelectedKeywords(filtered);
    }
  };
  const onDescriptiveTagClick = (item: MetadataItemType) => {
    const exists = !!selectedDescriptiveMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedDescriptiveMetadata([...selectedDescriptiveMetadata, item]);
    } else {
      const filtered = selectedDescriptiveMetadata.filter((w) => w.id !== item.id);
      setSelectedDescriptiveMetadata(filtered);
    }
  };
  const onBusinessTagClick = (item: MetadataItemType) => {
    const exists = !!selectedBusinessMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedBusinessMetadata([...selectedBusinessMetadata, item]);
    } else {
      const filtered = selectedBusinessMetadata.filter((w) => w.id !== item.id);
      setSelectedBusinessMetadata(filtered);
    }
  };
  const createMetadata = async (name: string, type: keyof typeof CREATE_ENDPOINT) => {
    const API_URL = CREATE_ENDPOINT[type];
    try {
      const res = await apiService.post(API_URL, {
        data: { name, item_type: category?.toUpperCase() }
      });
      if (res.status === 201) {
        const { id } = res.data.data;
        toast(<ToastMessage type='success' message={res.data.message} />);
        MAP_LOCAL_STATE[type]((p) => [{ name, id }, ...p]);
      }
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  const createKeywordMetadata = async (
    name: string,
    type: keyof typeof CREATE_ENDPOINT,
    popular: boolean
  ) => {
    const API_URL = CREATE_ENDPOINT[type];
    try {
      const res = await apiService.post(API_URL, {
        data: { name, type, popular }
      });
      if (res.status === 201) {
        const { id } = res.data.data;
        toast(<ToastMessage type='success' message={res.data.message} />);
        setKeywordsList((p) => [{ name, id }, ...p]);
      }
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  useEffect(() => {
    const formattedKeywords = selectedKeywords.map((keyword) => {
      return {
        id: keyword.id,
        name: keyword.name
      };
    });
    const formattedDescriptiveMetadata = selectedDescriptiveMetadata.map((descriptiveMetadata) => {
      return {
        id: descriptiveMetadata.id,
        name: descriptiveMetadata.name
      };
    });
    const formattedBusinessMetadata = selectedBusinessMetadata.map((businessMetadata) => {
      return {
        id: businessMetadata.id,
        name: businessMetadata.name
      };
    });
    setFormData({
      ...formData,
      meal_keywords: formattedKeywords,
      meal_descriptive_metadatas: formattedDescriptiveMetadata,
      meal_business_metadatas: formattedBusinessMetadata
    });
  }, [selectedKeywords, selectedDescriptiveMetadata, selectedBusinessMetadata]);
  return (
    <div>
      <MetadataDropdown
        title='Keywords'
        description='Select keyword'
        metadataList={keywordsList}
        selectedMetadata={selectedKeywords}
        onCreate={(w, popular) => createKeywordMetadata(w, 'meal', popular)}
        onTagClick={onKeywordTagClick}
      />
      <MetadataDropdown
        title='Descriptive Metadata'
        description='Select descriptive metadata'
        metadataList={descriptiveMetadataList}
        selectedMetadata={selectedDescriptiveMetadata}
        onCreate={(w) => createMetadata(w, 'descriptive')}
        onTagClick={onDescriptiveTagClick}
      />
      <MetadataDropdown
        title='Business Metadata'
        description='Select Business metadata'
        metadataList={businessMetadataList}
        selectedMetadata={selectedBusinessMetadata}
        onCreate={(w) => createMetadata(w, 'business')}
        onTagClick={onBusinessTagClick}
      />
    </div>
  );
};

export default MealMetadataInfo;
